import { JobDetailsSkeleton } from "components/job/JobDetailsSkeleton";
import { useCallback, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { setPage, setSelectedJob } from "store/root.store";
import { getCachePrefill } from "store/slices/cache.store";
import {
  checkServerSavedData,
  getComments,
  getFiles,
  getJob,
  getLinks,
  getNotes,
  initializeVisit,
  selectJobLoaded,
  selectVisitLoaded,
  setJobTab,
  setSelectedJob as setVisitSelectedJob,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

interface JobProviderProps {
  jobId: string | undefined;
}

export default function JobProvider({ jobId }: JobProviderProps) {
  const dispatch = useAppDispatch();
  const jobLoaded = useAppSelector(selectJobLoaded);
  const visitLoaded = useAppSelector(selectVisitLoaded);
  const { jobs } = useAppSelector((state) => state.jobs);
  const { loading: cacheLoading } = useAppSelector((state) => state.cache);
  const commentsEnabled = useAppSelector((s) => isFlagEnabled(s, "Comments"));
  const documentWeblinksEnabled = useAppSelector((s) => isFlagEnabled(s, "DocumentWebLinks"));
  const serverSavedVisitDataEnabled = useAppSelector(
    (s) => isFlagEnabled(s, "ServerSavedVisitData") && s.user.serverSavedVisitData,
  );
  const { state }: any = useLocation();

  const handleServerData = useCallback(() => {
    dispatch(checkServerSavedData());
  }, [dispatch]);

  const initializeJob = useCallback(async () => {
    await dispatch(getJob()).catch(() => {
      dispatch(addSnackbarMessage({ key: "GetJob-fail" }));
    });
    dispatch(getFiles());
    dispatch(getNotes());
    if (commentsEnabled) dispatch(getComments());
    if (documentWeblinksEnabled) dispatch(getLinks());
  }, [commentsEnabled, dispatch, documentWeblinksEnabled]);

  const setJobId = useCallback(async () => {
    dispatch(setPage({ page: "jobs" }));
    await dispatch(setVisitSelectedJob({ jobId }));
    dispatch(setSelectedJob({ jobId }));
  }, [dispatch, jobId]);

  useEffect(() => {
    const job = jobId ? jobs[jobId] : null;
    if (!jobLoaded || !job || cacheLoading.prefill) return;
    dispatch(
      initializeVisit({
        job,
      }),
    );
  }, [cacheLoading.prefill, dispatch, jobId, jobLoaded, jobs]);

  useEffect(() => {
    if (!jobId) return;
    setJobId();
    initializeJob();
    dispatch(setJobTab({ tab: "details" }));
  }, [dispatch, initializeJob, jobId, setJobId]);

  useEffect(() => {
    if (!visitLoaded || !serverSavedVisitDataEnabled) return;
    handleServerData();
  }, [handleServerData, serverSavedVisitDataEnabled, visitLoaded]);

  useEffect(() => {
    dispatch(getCachePrefill({ force: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!jobLoaded || !visitLoaded) return;
    if (state?.openComment) dispatch(setJobTab({ tab: "comments" }));
  }, [dispatch, jobLoaded, state?.openComment, visitLoaded]);

  if (!jobLoaded || !visitLoaded) {
    return <JobDetailsSkeleton />;
  }

  return <Outlet />;
}
